import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SessionType } from '@shared';
import { selectAdvisorAdminPortalState, selectSession } from '@/model/selectors';
import { startImpersonation, stopImpersonation } from '@/model/impersonationModel';
import { AdminModeToggle } from './AdminModeToggle';
import { AdminSearchBar } from './AdminSearchBar';
import { SelectedAdvisorBar } from './SelectedAdvisorBar';

export function AdminModeBanner() {
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const { selectedAdvisor } = useSelector(selectAdvisorAdminPortalState);
  const [isEnabled, setIsEnabled] = useState(false);

  const isWizardUser = session.sessionType === SessionType.wizard;
  const isImpersonating = (isWizardUser && session.impersonation?.isImpersonating) ?? false;

  // Show the toggle as enabled if admin mode is toggled on OR we're currently impersonating
  const showEnabled = isEnabled || isImpersonating;

  const handleToggleChange = useCallback(
    (checked: boolean) => {
      setIsEnabled(checked);
      if (!checked) {
        dispatch(stopImpersonation());
      }
    },
    [dispatch],
  );

  const handleAdvisorSelected = useCallback(
    (advisor: Advisor) => {
      dispatch(startImpersonation(advisor.advisorId));
    },
    [dispatch],
  );

  const handleChangeAdvisor = useCallback(() => {
    dispatch(stopImpersonation());
  }, [dispatch]);

  if (!isWizardUser) {
    return null;
  }

  return (
    <div
      className={`sticky top-0 z-40 -mx-4 border-b px-4 py-2 sm:-mx-6 sm:px-6 lg:px-8 ${
        showEnabled ? 'border-primary-300 bg-primary' : 'border-transparent bg-gray-50'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pl-2 sm:pl-6">
        <AdminModeToggle
          enabled={showEnabled}
          onToggle={handleToggleChange}
        />

        {showEnabled && (
          <>
            <div className="bg-primary-300 hidden h-4 w-px sm:block" />
            {isImpersonating && selectedAdvisor ? (
              <SelectedAdvisorBar
                advisor={selectedAdvisor}
                onChangeAdvisor={handleChangeAdvisor}
              />
            ) : isImpersonating && session.advisor ? (
              <SelectedAdvisorBar
                advisor={session.advisor}
                onChangeAdvisor={handleChangeAdvisor}
              />
            ) : (
              <AdminSearchBar onAdvisorSelected={handleAdvisorSelected} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
